import React, { useState, useEffect } from 'react';
import apiConfig from '../../config/apiConfig';
import './ManageCities.css';

const ManageCities = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCity, setEditingCity] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    xPosition: 0,
    yPosition: 0,
    alignment: 0,
    owner: ''
    // Removed description field as it's not supported by the API
  });
  const [filterText, setFilterText] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  // Fetch all cities when component mounts
  useEffect(() => {
    fetchCities();
  }, []);

  // Fetch cities from the API
  const fetchCities = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiConfig.cities, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cities: ${response.status}`);
      }

      const data = await response.json();
      setCities(data);
    } catch (err) {
      console.error('Error fetching cities:', err);
      setError(err.message || 'Failed to load cities');
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes for form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Convert number fields from string to number
    if (name === 'xPosition' || name === 'yPosition' || name === 'alignment') {
      setFormData({
        ...formData,
        [name]: Number(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Start editing a city
  const handleEdit = (city) => {
    setEditingCity(city);
    setIsCreating(false);
    setFormData({
      name: city.name || '',
      xPosition: city.xPosition ?? 0,
      yPosition: city.yPosition ?? 0,
      alignment: city.alignment ?? 0,
      owner: city.owner || ''
      // Removed description field as it's not supported by the API
    });
  };

  // Start creating a new city
  const handleCreate = () => {
    setEditingCity(null);
    setIsCreating(true);
    setFormData({
      name: '',
      xPosition: 0,
      yPosition: 0,
      alignment: 0,
      owner: ''
      // Removed description field as it's not supported by the API
    });
  };

  // Cancel editing/creating
  const handleCancel = () => {
    setEditingCity(null);
    setIsCreating(false);
  };

  // Confirm city deletion
  const confirmDelete = (city) => {
    setDeleteConfirmation(city);
  };

  // Cancel delete confirmation
  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  // Delete a city
  const handleDelete = async (cityId) => {
    try {
      setLoading(true);
      const response = await fetch(apiConfig.deleteCity(cityId), {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete city: ${response.status}`);
      }

      // Remove city from state
      setCities(cities.filter(city => city.id !== cityId));
      setDeleteConfirmation(null);
    } catch (err) {
      console.error('Error deleting city:', err);
      setError(err.message || 'Failed to delete city');
    } finally {
      setLoading(false);
    }
  };

  // Save city (create or update)
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Create API-compatible payload with only the fields the API expects
      const apiPayload = {
        name: formData.name,
        xPosition: Number(formData.xPosition),
        yPosition: Number(formData.yPosition),
        alignment: Number(formData.alignment)
      };
      
      // Only include owner if it's not empty
      if (formData.owner && formData.owner.trim() !== '') {
        apiPayload.owner = formData.owner.trim();
      }
      
      console.log('Saving city with payload:', apiPayload);
      
      // Create new city
      if (isCreating) {
        const response = await fetch(apiConfig.cities, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiPayload)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to create city: ${response.status} - ${errorText}`);
        }

        const newCity = await response.json();
        setCities([...cities, newCity]);
        setIsCreating(false);
      } 
      // Update existing city
      else if (editingCity) {
        const response = await fetch(apiConfig.updateCity(editingCity.id), {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiPayload)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to update city: ${response.status} - ${errorText}`);
        }

        const updatedCity = await response.json();
        
        // Update city in state
        setCities(cities.map(city => 
          city.id === editingCity.id ? updatedCity : city
        ));
        
        setEditingCity(null);
      }
    } catch (err) {
      console.error('Error saving city:', err);
      setError(err.message || 'Failed to save city');
    } finally {
      setLoading(false);
    }
  };

  // Get city icon based on alignment
  const getCityIcon = (alignment) => {
    if (!alignment && alignment !== 0) return '🏙️';
    if (alignment > 30) return '✨';
    if (alignment > 10) return '🏰';
    if (alignment > 0) return '🏘️';
    if (alignment === 0) return '🏙️';
    if (alignment > -10) return '🏚️';
    if (alignment > -30) return '🏚️';
    return '🏴';
  };

  // Get alignment text
  const getAlignmentText = (alignment) => {
    if (!alignment && alignment !== 0) return 'Unknown';
    if (alignment > 30) return 'Virtuous';
    if (alignment > 10) return 'Good';
    if (alignment > 0) return 'Slightly Good';
    if (alignment === 0) return 'Neutral';
    if (alignment > -10) return 'Slightly Evil';
    if (alignment > -30) return 'Evil';
    return 'Wicked';
  };

  // Filter cities based on filter text
  const filteredCities = cities.filter(city => {
    if (!filterText) return true;
    const searchTerm = filterText.toLowerCase();
    
    return (
      city.name?.toLowerCase().includes(searchTerm) ||
      city.owner?.toLowerCase().includes(searchTerm) ||
      getAlignmentText(city.alignment).toLowerCase().includes(searchTerm)
    );
  });

  return (
    <div className="admin-page city-management">
      <div className="admin-header">
        <h2>Manage Cities</h2>
        <button 
          className="create-button"
          onClick={handleCreate}
          disabled={isCreating || editingCity}
        >
          Create New City
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* City form (create or edit) */}
      {(isCreating || editingCity) && (
        <div className="city-form-container">
          <h3>{isCreating ? 'Create New City' : `Edit City: ${editingCity.name}`}</h3>
          <form onSubmit={handleSave} className="city-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">City Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="owner">City Owner (UUID)</label>
                <input
                  type="text"
                  id="owner"
                  name="owner"
                  value={formData.owner}
                  onChange={handleInputChange}
                  placeholder="Optional - Character UUID"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="xPosition">X Position</label>
                <input
                  type="number"
                  id="xPosition"
                  name="xPosition"
                  value={formData.xPosition}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="yPosition">Y Position</label>
                <input
                  type="number"
                  id="yPosition"
                  name="yPosition"
                  value={formData.yPosition}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="alignment">Alignment</label>
                <input
                  type="number"
                  id="alignment"
                  name="alignment"
                  value={formData.alignment}
                  onChange={handleInputChange}
                />
                <span className="alignment-preview">
                  {getCityIcon(formData.alignment)} {getAlignmentText(formData.alignment)}
                </span>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="save-button">
                {isCreating ? 'Create City' : 'Save Changes'}
              </button>
              <button type="button" className="cancel-button" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Cities list */}
      <div className="cities-list-container">
        <div className="filter-container">
          <input
            type="text"
            placeholder="Search cities..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="filter-input"
          />
          <span className="filter-count">
            {filteredCities.length} {filteredCities.length === 1 ? 'city' : 'cities'}
          </span>
        </div>

        {loading && !isCreating && !editingCity ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading cities...</p>
          </div>
        ) : filteredCities.length > 0 ? (
          <table className="cities-table">
            <thead>
              <tr>
                <th>Icon</th>
                <th>Name</th>
                <th>Alignment</th>
                <th>Position</th>
                <th>Owner</th>
                <th>Buildings</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCities.map(city => (
                <tr key={city.id}>
                  <td className="city-icon-cell">
                    {getCityIcon(city.alignment)}
                  </td>
                  <td>{city.name}</td>
                  <td>
                    {getAlignmentText(city.alignment)}
                    <span className="alignment-value">({city.alignment})</span>
                  </td>
                  <td>X: {city.xPosition ?? '?'}, Y: {city.yPosition ?? '?'}</td>
                  <td>{city.owner || 'None'}</td>
                  <td>{city.buildings?.length || 0}</td>
                  <td className="actions-cell">
                    <button className="action-button edit" onClick={() => handleEdit(city)}>
                      Edit
                    </button>
                    <button className="action-button delete" onClick={() => confirmDelete(city)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-results">
            {filterText ? 'No cities match your search' : 'No cities found'}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmation && (
        <div className="modal-backdrop">
          <div className="delete-confirmation-modal">
            <h3>Delete City</h3>
            <p>
              Are you sure you want to delete <strong>{deleteConfirmation.name}</strong>?
            </p>
            <p className="warning">This action cannot be undone!</p>
            <div className="modal-actions">
              <button 
                className="delete-confirm-button"
                onClick={() => handleDelete(deleteConfirmation.id)}
              >
                Delete
              </button>
              <button 
                className="cancel-button"
                onClick={cancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCities;
